import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

import { ArrowLeft, Save } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useUserName, useTienePermiso} from '../App';
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { RechazoModal } from "./RechazoModal";

interface NMXA105E04FormData {
  // Información general
  numeroSolicitud: string
  fecha: string
  posicionEspecimenes: string
  diacetato_acido_label?: string
  diacetato_alcalino_label?: string
  realizo: string
  superviso: string
  
  // ÁCIDO
  diacetato_acido: string
  algodon_blanqueado_acido: string
  poliamida_acido: string
  poliester_acido: string
  acrilico_acido: string
  lana_acido: string
  cambio_color_acido: string
  
  // ALCALINO
  diacetato_alcalino: string
  algodon_blanqueado_alcalino: string
  poliamida_alcalino: string
  poliester_alcalino: string
  acrilico_alcalino: string
  lana_alcalino: string
  cambio_color_alcalino: string
  
  // Observaciones
  observaciones: string
  mostrarObservaciones: boolean

  FORMA_EVALUACION?: string;
  METODO_C?: string;
  TIPO_MULTIFIBRA?: string;
}

interface NMXA105E04FormProps {
  mode: "create" | "view" | "edit"
  initialData?: any
  realizoUser?: string // NUEVO
  onSave?: (data: any) => void
  onCancel?: () => void
  onRefreshBackend?: () => void
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA105E04Form({
  mode,
  initialData = {},
  realizoUser, // NUEVO
  onSave,
  onCancel,
  onRefreshBackend,
  pedidoRowId,
  onAutosave,
}: NMXA105E04FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = globalRol;

  // Lógica robusta para initialData
  const initialDataObj = Array.isArray(initialData) ? initialData[0] : initialData || {};
  // Inicialización robusta de realizo
  const [formData, setFormData] = useState<any>({
    ...initialDataObj,
    realizo: (mode === 'create' && realizoUser) ? realizoUser : (initialDataObj.realizo || ''),
    FORMA_EVALUACION: initialDataObj.FORMA_EVALUACION || initialDataObj.forma_evaluacion || '',
    METODO_C: initialDataObj.METODO_C || initialDataObj.metodo_c || '',
    TIPO_MULTIFIBRA: initialDataObj.TIPO_MULTIFIBRA || initialDataObj.tipo_multifibra || '',
    mostrarObservaciones: initialDataObj.MOSTRAR_OBSERVACIONES === true,
  });

  // Estado de edición y estatus robusto
  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>((initialDataObj as any)?.ACEPTADO || '');

  // Estados para el modal de rechazo
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData((prev: any) => {
      const merged = { ...prev, ...initialData };
      merged.FORMA_EVALUACION = initialDataObj.FORMA_EVALUACION || initialDataObj.forma_evaluacion || prev.FORMA_EVALUACION || '';
      merged.METODO_C = initialDataObj.METODO_C || initialDataObj.metodo_c || prev.METODO_C || '';
      merged.TIPO_MULTIFIBRA = initialDataObj.TIPO_MULTIFIBRA || initialDataObj.tipo_multifibra || prev.TIPO_MULTIFIBRA || '';
      merged.mostrarObservaciones = initialDataObj.MOSTRAR_OBSERVACIONES === true;
      return merged;
    });
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialDataObj as any)?.ACEPTADO ?? (initialDataObj as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';

  // Si hay realizoUser y estamos creando, asignar automáticamente SOLO si no hay valor en initialData.realizo y tampoco en formData.realizo
  useEffect(() => {
    if (mode === 'create' && realizoUser && !(initialData?.realizo) && !formData.realizo) {
      setFormData((prev: any) => ({ ...prev, realizo: realizoUser }))
    }
  }, [realizoUser, mode, initialData?.realizo, formData.realizo])

  // isReadOnly robusto
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  const handleInputChange = (field: keyof NMXA105E04FormData, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formData)
      }
    } finally {
      setGuardando(false)
    }
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData as any
    return rest
  }

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "Crear NMX-A-105-E04-INNTEX-2019"
      case "edit":
        return "Editar NMX-A-105-E04-INNTEX-2019"
      case "view":
        return "Ver NMX-A-105-E04-INNTEX-2019"
      default:
        return "NMX-A-105-E04-INNTEX-2019"
    }
  }

  // --- ADMIN ACTIONS ---
  // RowId robusto: primero formData, luego initialDataObj, luego vacío
  const [autosaveRowId, setAutosaveRowId] = useState<string>((formData as any)?.ROW_ID || (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialDataObj as any)?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId
  const pedidoValue = (formData as any).PEDIDO || (initialDataObj as any)?.PEDIDO || '';
  const pruebaValue = (formData as any).PRUEBA || (initialDataObj as any)?.PRUEBA || '';

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-105-E04-INNTEX-2019 SOLIDEZ DEL COLOR AL SUDOR',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado') => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-105-E04?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-105-E04?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, undefined, (payload as any).superviso, nomId);
      setEstatus(payload.ACEPTAR);
      setEditMode(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleRechazar = () => setRechazoModalOpen(true);

  const handleRechazoSubmit = async (razon: string) => {
    setRechazoLoading(true);
    const confirmMsg = 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-105-E04?';
    if (!window.confirm(confirmMsg)) {
      setRechazoLoading(false);
      setRechazoModalOpen(false);
      return;
    }
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'rechazado',
      ACEPTADO: 'rechazado',
      RAZON_RECHAZO: razon
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId);
      setEstatus('rechazado');
      setEditMode(false);
      setRechazoModalOpen(false);
      toast.success('Prueba rechazada correctamente.');
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
    }
  };

  const handleEditar = () => setEditMode(true);

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-105-E04? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        onSubmit={handleRechazoSubmit}
        loading={rechazoLoading}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        <div className="flex gap-2">
          {/* Admin action buttons */}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={handleRechazar}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {/* Botón guardar en modo create */}
          {mode === 'create' && (
            <Button onClick={handleSave} className="gap-2" disabled={guardando}>
              <Save className="h-4 w-4" />
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-center text-lg">
            NMX-A-105-E04-INNTEX-2019 SOLIDEZ DEL COLOR AL SUDOR
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Información General */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <tbody>
                {/* Primera fila */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    NO. DE SOLICITUD:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Número de solicitud"
                      value={formData.numeroSolicitud}
                      onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    FECHA:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* Segunda fila */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    POSICIÓN DE LOS ESPECÍMENES:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Posición de los especímenes"
                      value={formData.posicionEspecimenes}
                      onChange={(e) => handleInputChange('posicionEspecimenes', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    REALIZÓ:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Nombre del responsable"
                      value={formData.realizo}
                      readOnly
                      className="bg-muted-foreground/10 cursor-not-allowed border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* Tercera fila */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium" colSpan={2}>
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    SUPERVISÓ:
                  </td>
                  <td className="border border-border p-1">
                    <div className="rounded bg-gray-50 px-2 py-1 text-center font-semibold text-gray-700 border border-gray-200 min-h-[38px] flex items-center justify-center">
                      {formData.superviso || <span className="text-gray-400">Sin asignar</span>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Tabla Principal - ÁCIDO y ALCALINO */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <tbody>
                {/* Encabezados principales */}
                <tr>
                  <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                    ÁCIDO
                  </td>
                  <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                    ALCALINO
                  </td>
                </tr>

                {/* Subencabezados */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium text-center">
                    FIBRA
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium text-center">
                    TRANSFERENCIA
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium text-center">
                    FIBRA
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium text-center">
                    TRANSFERENCIA
                  </td>
                </tr>

                {/* diacetato */}
                <tr>
                  <td className="border border-border p-1 bg-muted">
                    <Input
                      type="text"
                      value={formData.diacetato_acido_label ?? 'diacetato (acetato)'}
                      onChange={(e) => handleInputChange('diacetato_acido_label', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent font-medium"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.diacetato_acido}
                      onChange={(e) => handleInputChange('diacetato_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1 bg-muted">
                    <Input
                      type="text"
                      value={formData.diacetato_alcalino_label ?? 'diacetato (acetato)'}
                      onChange={(e) => handleInputChange('diacetato_alcalino_label', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent font-medium"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.diacetato_alcalino}
                      onChange={(e) => handleInputChange('diacetato_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* ALGODÓN blanqueado */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    ALGODÓN blanqueado
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.algodon_blanqueado_acido}
                      onChange={(e) => handleInputChange('algodon_blanqueado_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    ALGODÓN blanqueado
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.algodon_blanqueado_alcalino}
                      onChange={(e) => handleInputChange('algodon_blanqueado_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* poliamida */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    poliamida
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.poliamida_acido}
                      onChange={(e) => handleInputChange('poliamida_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    poliamida
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.poliamida_alcalino}
                      onChange={(e) => handleInputChange('poliamida_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* poliéster */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    poliéster
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.poliester_acido}
                      onChange={(e) => handleInputChange('poliester_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    poliéster
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.poliester_alcalino}
                      onChange={(e) => handleInputChange('poliester_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* acrílico */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    acrílico
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.acrilico_acido}
                      onChange={(e) => handleInputChange('acrilico_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    acrílico
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.acrilico_alcalino}
                      onChange={(e) => handleInputChange('acrilico_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* lana */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    lana
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.lana_acido}
                      onChange={(e) => handleInputChange('lana_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    lana
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.lana_alcalino}
                      onChange={(e) => handleInputChange('lana_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>

                {/* CAMBIO DE COLOR */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    CAMBIO DE COLOR
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.cambio_color_acido}
                      onChange={(e) => handleInputChange('cambio_color_acido', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    CAMBIO DE COLOR
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Valor"
                      value={formData.cambio_color_alcalino}
                      onChange={(e) => handleInputChange('cambio_color_alcalino', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <div className="border border-border p-2 bg-muted font-medium flex items-center gap-2">
              OBSERVACIONES:
              <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </div>
            <Textarea
              placeholder="Observaciones adicionales sobre la prueba..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              disabled={isReadOnly}
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Parámetros adicionales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="formaEvaluacion" className="block text-sm font-medium text-gray-700">Forma de evaluación</label>
              <Input
                id="formaEvaluacion"
                type="text"
                placeholder="Ingrese la forma de evaluación"
                value={formData.FORMA_EVALUACION || ""}
                onChange={e => handleInputChange('FORMA_EVALUACION', e.target.value)}
                disabled={isReadOnly}
                className="mt-2"
              />
            </div>
            <div>
              <label htmlFor="metodoC" className="block text-sm font-medium text-gray-700">Opción utilizada</label>
              <Input
                id="metodoC"
                type="text"
                placeholder="Ingrese la opción utilizada"
                value={formData.METODO_C || ""}
                onChange={e => handleInputChange('METODO_C', e.target.value)}
                disabled={isReadOnly}
                className="mt-2"
              />
            </div>
            <div>
              <label htmlFor="tipoMultifibra" className="block text-sm font-medium text-gray-700">Tipo de Multifibra</label>
              <Input
                id="tipoMultifibra"
                type="text"
                placeholder="Ingrese el tipo de multifibra"
                value={formData.TIPO_MULTIFIBRA || ""}
                onChange={e => handleInputChange('TIPO_MULTIFIBRA', e.target.value)}
                disabled={isReadOnly}
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}